import { useState, type ReactNode } from "react";
import type { FilterState } from "@/utils/movie";
import { GENRES, REGIONS, SORTS, getYearOptions } from "@/utils/movie";

interface TypeOption {
  value: string;
  label: string;
}

interface FilterBarProps {
  typeOptions: TypeOption[];
  currentType: string;
  onTypeChange: (value: string) => void;
  filters: FilterState;
  onFilterChange: (key: keyof FilterState, value: string) => void;
  onReset: () => void;
  extraSlot?: ReactNode;
}

const YEAR_OPTIONS = getYearOptions();

function PillGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:gap-4">
      <span className="shrink-0 pt-1 text-sm text-foreground-500 sm:w-12">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer ${
              value === opt.value
                ? "bg-primary-500 font-medium text-background-50"
                : "text-foreground-700 hover:bg-background-200/70"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({
  typeOptions,
  currentType,
  onTypeChange,
  filters,
  onFilterChange,
  onReset,
  extraSlot,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const yearOptions = YEAR_OPTIONS.map((y) => ({ value: y, label: y }));
  const sortOptions = SORTS.map((s) => ({ value: s.value, label: s.label }));
  const regionOptions = REGIONS.map((r) => ({ value: r, label: r }));
  const genreOptions = GENRES.map((g) => ({ value: g, label: g }));

  const activeCount = [
    filters.region !== "全部",
    filters.genre !== "全部",
    filters.year !== "全部",
    filters.sort !== "latest",
  ].filter(Boolean).length;

  return (
    <div className="rounded-lg border border-background-200/80 bg-background-100/60">
      <div className="flex items-center justify-between gap-4 border-b border-background-200/70 px-4 py-3 md:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 bg-background-50 px-3 py-2 text-sm text-foreground-800 cursor-pointer"
        >
          <i className="ri-filter-3-line text-base"></i>
          筛选条件
          {activeCount > 0 && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-[11px] text-background-50">
              {activeCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={onReset}
          className="whitespace-nowrap text-sm text-foreground-500 cursor-pointer"
        >
          重置
        </button>
      </div>

      <div className={`${open ? "block" : "hidden"} px-4 py-2 md:block md:px-5`}>
        <div className="divide-y divide-background-200/60">
          <PillGroup
            label="类型"
            options={typeOptions}
            value={currentType}
            onChange={onTypeChange}
          />
          <PillGroup
            label="地区"
            options={regionOptions}
            value={filters.region}
            onChange={(v) => onFilterChange("region", v)}
          />
          <PillGroup
            label="题材"
            options={genreOptions}
            value={filters.genre}
            onChange={(v) => onFilterChange("genre", v)}
          />
          <PillGroup
            label="年份"
            options={yearOptions}
            value={filters.year}
            onChange={(v) => onFilterChange("year", v)}
          />
          <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <PillGroup
              label="排序"
              options={sortOptions}
              value={filters.sort}
              onChange={(v) => onFilterChange("sort", v)}
            />
            {extraSlot}
          </div>
        </div>
      </div>
    </div>
  );
}